import { INotificationSettings, INotificationSettingsProps } from '@cscglobal-tbs/notifications-ui';
import { IEnterprise } from '@cscglobal-tbs/react-components-core';
import { useRef, useState } from 'react';
import { useGetEnterprise } from '../api/enterprise/enterprise';
import { useIsFeatureEnabled } from '../api/features/features';
import { useGetNotificationSettings, useUpdateNotificationSettings } from '../api/notifications/notifications';

export const useNotificationSettingsProps = (isNotificationPopoverOpen: boolean) => {
    const isQueryEnabled = useRef(false);
    const { data: isNotificationSettingsEnabled } = useIsFeatureEnabled({
        featureFlagName: 'NotificationSettingsEnabled',
    });
    const { data: enterprise } = useGetEnterprise();

    // Fetch notification settings only if the enterprise is available, feature is enabled, and the popover is open
    if (enterprise && isNotificationSettingsEnabled && isNotificationPopoverOpen) {
        isQueryEnabled.current = true;
    }

    const {
        data: notificationSettingsResponse,
        isLoading: isGetNotificationSettingsLoading,
        isFetching: isGetNotificationSettingsFetching,
        isError: isGetNotificationSettingsError,
        refetch: refetchGetNotificationSettings,
    } = useGetNotificationSettings({ query: { enabled: isQueryEnabled.current } });

    const { mutateAsync: notificationSettingsMutation } = useUpdateNotificationSettings();

    const [isSavingOrRefetchingSettings, setIsSavingOrRefetchingSettings] = useState(false);
    const [isFetchErrorDismissed, setIsFetchErrorDismissed] = useState(false);
    const [isMutationError, setIsMutationError] = useState(false);
    const isLoading = isGetNotificationSettingsLoading || isGetNotificationSettingsFetching;
    const isFetchError = isGetNotificationSettingsError;

    let settingsErrorText: string | undefined = undefined;

    if (!isSavingOrRefetchingSettings) {
        if (isFetchError && !isFetchErrorDismissed) {
            // If there is a fetch error and it hasn't been dismissed, set the error text
            settingsErrorText = 'There was an error fetching your notification settings. Please try again later.';
        } else if (isMutationError) {
            // If there is a mutation error, set the error text (note, when user dismisses the error, we reset the isMutationError to false)
            settingsErrorText = 'There was an error in your attempted change. Please try again later.';
        }
    }

    const updateNotificationSettings = (newSettings: INotificationSettings) => {
        setIsSavingOrRefetchingSettings(true);
        setIsMutationError(false); //reset the mutation error state
        setIsFetchErrorDismissed(true); //hide any previous fetch error message

        notificationSettingsMutation(
            { data: newSettings },
            {
                onSuccess: async () => {
                    //reset the isFetchErrorDismissed state to false so that the error message can be shown again if needed
                    setIsFetchErrorDismissed(false);
                    await refetchGetNotificationSettings();
                    setIsSavingOrRefetchingSettings(false);
                },
                onError: (error) => {
                    console.error('Error updating notification settings:', error);
                    setIsMutationError(true);
                    setIsSavingOrRefetchingSettings(false);
                },
            }
        );
    };

    if (enterprise && isNotificationSettingsEnabled) {
        const notificationSettingsProps: INotificationSettingsProps = {
            enterprise: enterprise as unknown as IEnterprise,
            isLoading,
            isFetchError,
            notificationSettings: notificationSettingsResponse?.notificationSettings as INotificationSettings,
            isSavingOrRefetchingSettings: isSavingOrRefetchingSettings,
            updateNotificationSettings: updateNotificationSettings,
            errorMessageBarProps: {
                errorText: settingsErrorText,
                onErrorDismissed: () => {
                    setIsFetchErrorDismissed(true); //hide any fetch error message
                    setIsMutationError(false); //reset the mutation error state
                },
                // if the error was on fetch/refetch, we want to enable retrying
                onHandleRefetchData: isFetchError
                    ? () => {
                          //reset the isFetchErrorDismissed state to false so that the error message can be shown again if needed
                          setIsFetchErrorDismissed(false);
                          refetchGetNotificationSettings();
                      }
                    : undefined,
            },
        };

        return { notificationSettingsProps };
    } else {
        return { notificationSettingsProps: null };
    }
};
